import { computeCandidate, rankCandidates, } from './selection.js';
export const RANKING_STATUSES = {
    draft: 'Brouillon',
    under_control: 'En contrôle',
    approved: 'Approuvé',
    published: 'Publié',
    superseded: 'Remplacé',
};
/**
 * Regroupe les corrections d'un candidat en notes par discipline. Des saisies
 * contradictoires (présent pour l'un, absent pour l'autre) ne sont jamais
 * arbitrées par le calcul : elles sont signalées.
 */
export function buildMarks(records, disciplines) {
    const marks = {};
    const conflicts = [];
    for (const d of disciplines) {
        const rs = records.filter((r) => r.disciplineCode === d.code).sort((a, b) => a.round - b.round);
        if (!rs.length)
            continue;
        const statuses = new Set(rs.map((r) => r.status));
        if (statuses.size > 1) {
            conflicts.push(`${d.label} : saisies contradictoires entre correcteurs (présence).`);
            continue;
        }
        const status = rs[0].status;
        if (status !== 'present') {
            marks[d.code] = { status };
            continue;
        }
        if (d.grading === 'status') {
            const codes = new Set(rs.map((r) => r.code));
            if (codes.size > 1) {
                conflicts.push(`${d.label} : statuts différents selon les correcteurs.`);
                continue;
            }
            marks[d.code] = { status, statusCode: rs[0].code ?? undefined };
            continue;
        }
        const values = rs
            .map((r) => (d.grading === 'performance' ? r.performance : r.value))
            .filter((v) => v !== null);
        marks[d.code] = { status, values };
    }
    const absentEverywhere = disciplines.length > 0 && disciplines.every((d) => marks[d.code]?.status === 'absent');
    return { marks, conflicts, absentEverywhere };
}
/** Étape non notée : décision par statut (ou manuelle), jamais par un calcul. */
function statusStage(inputs, stage, rule) {
    return inputs.map((i) => {
        const rs = i.records.filter((r) => r.disciplineCode === null);
        const base = {
            id: i.id,
            category: i.category,
            score: null,
            rank: null,
            categoryRank: null,
            tie: false,
            decision: 'incomplete',
            reasons: [],
            disciplines: [],
        };
        if (!rs.length)
            return { ...base, reasons: ['Statut non renseigné.'] };
        const statuses = new Set(rs.map((r) => (r.status === 'present' ? `p:${r.code}` : r.status)));
        if (statuses.size > 1)
            return { ...base, reasons: ['Statuts différents selon les évaluateurs.'] };
        const r = rs[0];
        if (r.status === 'absent')
            return { ...base, decision: 'absent', reasons: ['Absent.'] };
        if (r.status === 'exempt')
            return { ...base, decision: 'pending', reasons: ['Dispensé : décision humaine requise.'] };
        const option = stage.statuses.find((s) => s.code === r.code);
        if (!option)
            return { ...base, reasons: ['Statut inconnu.'] };
        if (rule.mode === 'manual')
            return { ...base, decision: 'pending', reasons: [`Statut : ${option.label}.`] };
        return option.passes
            ? { ...base, decision: 'passed', reasons: [`Statut : ${option.label}.`] }
            : { ...base, decision: 'failed', reasons: [`Statut : ${option.label}.`] };
    });
}
/**
 * Résultats d'une étape : notes, éliminations, classement général et, si la
 * règle le prévoit, classement et quotas PAR CATÉGORIE (places de chaque
 * catégorie du concours).
 */
export function computeStageResults(inputs, stage, rule, categories = []) {
    if (!stage.graded)
        return statusStage(inputs, stage, rule);
    const prepared = inputs.map((i) => {
        const { marks, conflicts, absentEverywhere } = buildMarks(i.records, stage.disciplines);
        const outcome = computeCandidate(stage.disciplines, marks, rule, i.gender);
        if (conflicts.length) {
            outcome.incomplete = true;
            outcome.score = null;
            outcome.reasons.push(...conflicts);
        }
        return { input: i, outcome, absentEverywhere };
    });
    // Classement général (ordre et ex æquo), décisions selon la règle.
    const general = rankCandidates(prepared.map((p) => ({ id: p.input.id, outcome: p.outcome, birthDate: p.input.birthDate })), rule.byCategory
        ? { ...rule, mode: 'manual', supplementary: { enabled: false, size: null } }
        : rule);
    const byId = new Map(general.map((g) => [g.id, g]));
    const entries = new Map();
    for (const p of prepared) {
        const g = byId.get(p.input.id);
        entries.set(p.input.id, {
            id: p.input.id,
            category: p.input.category,
            score: g.score,
            rank: g.rank,
            categoryRank: null,
            tie: g.tie,
            decision: g.decision,
            reasons: [...g.reasons],
            disciplines: p.outcome.disciplines,
        });
    }
    if (rule.byCategory) {
        const seats = new Map(categories.map((c) => [c.code, c.seats]));
        const groups = new Map();
        for (const p of prepared) {
            const key = p.input.category ?? '';
            groups.set(key, [...(groups.get(key) ?? []), p]);
        }
        for (const [code, group] of groups) {
            const e = (id) => entries.get(id);
            const quota = seats.get(code);
            if (!code || quota === undefined || (rule.mode !== 'threshold' && !quota)) {
                for (const p of group) {
                    const x = e(p.input.id);
                    if (x.decision === 'pending' || x.decision === 'failed') {
                        x.decision = 'pending';
                        x.reasons.push(code
                            ? 'Places de la catégorie non définies : décision humaine requise.'
                            : 'Catégorie non renseignée : décision humaine requise.');
                    }
                }
                continue;
            }
            const ranked = rankCandidates(group.map((p) => ({ id: p.input.id, outcome: p.outcome, birthDate: p.input.birthDate })), { ...rule, quota });
            for (const r of ranked) {
                const x = e(r.id);
                x.categoryRank = r.rank;
                x.decision = r.decision;
                x.reasons = r.reasons;
                x.tie = r.tie;
            }
        }
    }
    for (const p of prepared)
        if (p.absentEverywhere) {
            const x = entries.get(p.input.id);
            x.decision = 'absent';
            x.rank = null;
            x.categoryRank = null;
        }
    return inputs.map((i) => entries.get(i.id));
}
export function summarize(entries) {
    const counts = {
        passed: 0,
        supplementary: 0,
        failed: 0,
        eliminated: 0,
        incomplete: 0,
        absent: 0,
        pending: 0,
    };
    for (const e of entries)
        counts[e.decision]++;
    return { total: entries.length, counts, pending: counts.pending };
}
/** Différences entre deux versions d'un classement (recalcul). */
export function compareRankings(before, after) {
    const a = new Map(before.map((e) => [e.key, e]));
    const b = new Map(after.map((e) => [e.key, e]));
    const keys = [...new Set([...a.keys(), ...b.keys()])];
    const out = [];
    for (const key of keys) {
        const x = a.get(key) ?? null;
        const y = b.get(key) ?? null;
        const changes = [];
        if (!x)
            changes.push('added');
        else if (!y)
            changes.push('removed');
        else {
            if (x.rank !== y.rank)
                changes.push('rank');
            if (x.score !== y.score)
                changes.push('score');
            if (x.decision !== y.decision)
                changes.push('decision');
        }
        if (changes.length)
            out.push({ key, label: (y ?? x).label, before: x, after: y, changes });
    }
    return out.sort((p, q) => (p.after?.rank ?? Infinity) - (q.after?.rank ?? Infinity));
}
/** Décisions qu'un humain peut prendre sur une ligne « décision humaine requise ». */
export const MANUAL_DECISIONS = ['passed', 'supplementary', 'failed'];
/* ------------------------------------------------------------------ */
/* Contrôle, approbation, publication (phase 16, ADR-033)              */
/* ------------------------------------------------------------------ */
export const PUBLICATION_KINDS = {
    provisional: 'Résultats provisoires',
    final: 'Résultats définitifs',
};
export const RECTIFICATION_STATUSES = {
    requested: 'Demandée',
    accepted: 'Acceptée — nouvelle version à établir',
    rejected: 'Refusée',
    completed: 'Rectifiée (nouvelle version approuvée)',
};
/**
 * Contrôle des seuils et quotas avant approbation (cahier §9.9, étape 6) :
 * vérifie que les décisions retenues — y compris les décisions humaines —
 * respectent la règle appliquée.
 */
export function controlChecks(entries, rule, categories = [], graded = true) {
    const checks = [];
    const passed = entries.filter((e) => e.decision === 'passed');
    const extra = entries.filter((e) => e.decision === 'supplementary');
    const pending = entries.filter((e) => e.decision === 'pending').length;
    const incomplete = entries.filter((e) => e.decision === 'incomplete').length;
    checks.push(pending
        ? { level: 'error', message: `${pending} décision(s) humaine(s) en attente.` }
        : { level: 'ok', message: 'Aucune décision en attente.' });
    if (graded && ['quota', 'threshold_quota'].includes(rule.mode)) {
        if (rule.byCategory) {
            for (const c of categories) {
                const n = passed.filter((e) => e.category === c.code).length;
                if (c.seats !== null && n > c.seats)
                    checks.push({
                        level: 'error',
                        message: `Catégorie ${c.code} : ${n} admis pour ${c.seats} place(s).`,
                    });
            }
            if (!checks.some((c) => c.level === 'error' && c.message.startsWith('Catégorie')))
                checks.push({ level: 'ok', message: 'Places respectées dans chaque catégorie.' });
        }
        else if (rule.quota !== null && passed.length > rule.quota)
            checks.push({
                level: 'error',
                message: `${passed.length} admis pour ${rule.quota} place(s).`,
            });
        else
            checks.push({
                level: 'ok',
                message: `${passed.length} admis pour ${rule.quota ?? '—'} place(s).`,
            });
    }
    if (graded && rule.threshold !== null && ['threshold', 'threshold_quota'].includes(rule.mode)) {
        const below = [...passed, ...extra].filter((e) => e.score === null || e.score < rule.threshold);
        checks.push(below.length
            ? {
                level: 'error',
                message: `${below.length} admis ou en liste complémentaire sous le seuil ${rule.threshold}.`,
            }
            : { level: 'ok', message: `Tous les admis atteignent le seuil ${rule.threshold}.` });
    }
    if (rule.supplementary.enabled &&
        rule.supplementary.size !== null &&
        extra.length > rule.supplementary.size)
        checks.push({
            level: 'error',
            message: `Liste complémentaire : ${extra.length} pour ${rule.supplementary.size} place(s).`,
        });
    if (!rule.supplementary.enabled && extra.length)
        checks.push({ level: 'error', message: 'Liste complémentaire non prévue par la règle.' });
    if (incomplete)
        checks.push({
            level: 'warning',
            message: `${incomplete} candidat(s) au résultat incomplet (note manquante ou 3e correction attendue).`,
        });
    return checks;
}
//# sourceMappingURL=results.js.map